import React, { useEffect, useState, useRef } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { History, Search, Bookmark, Lock, Trash2, Play, Heart, BookMarked, CalendarClock, Settings, Camera, X } from 'lucide-react';
import { toast } from 'sonner';
import { mediaUrl } from '../utils/mediaUrl';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const UserProfilePage = () => {
  const [activeTab, setActiveTab] = useState('watchHistory');
  const { user, checkAuth } = useAuth();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);

  const [watchHistory, setWatchHistory] = useState([]);
  const [searchHistory, setSearchHistory] = useState([]);
  const [watchlist, setWatchlist] = useState([]);
  const [liked, setLiked] = useState([]);
  const [borrows, setBorrows] = useState([]);
  const [reservations, setReservations] = useState([]);
  const [loading, setLoading] = useState(true);

  const [pwCurrent, setPwCurrent] = useState('');
  const [pwNew, setPwNew] = useState('');
  const [pwConfirm, setPwConfirm] = useState('');
  const [changingPw, setChangingPw] = useState(false);
  const [uploadingAvatar, setUploadingAvatar] = useState(false);

  useEffect(() => {
    fetchAll();
  }, []);

  const fetchAll = async () => {
    try {
      const [whRes, shRes, wlRes, lkRes, brRes, rsRes] = await Promise.all([
        axios.get(`${API}/watch-history`, { withCredentials: true }),
        axios.get(`${API}/search-history`, { withCredentials: true }),
        axios.get(`${API}/watchlist`, { withCredentials: true }),
        axios.get(`${API}/likes`, { withCredentials: true }),
        axios.get(`${API}/borrows`, { withCredentials: true }),
        axios.get(`${API}/reservations`, { withCredentials: true })
      ]);
      setWatchHistory(whRes.data);
      setSearchHistory(shRes.data);
      setWatchlist(wlRes.data);
      setLiked(lkRes.data);
      setBorrows(brRes.data);
      setReservations(rsRes.data);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleClearSearchHistory = async () => {
    if (!window.confirm('Clear all search history?')) return;
    try {
      await axios.delete(`${API}/search-history`, { withCredentials: true });
      toast.success('Search history cleared');
      fetchAll();
    } catch (error) {
      toast.error('Failed to clear');
    }
  };

  const handleRemoveFromWatchlist = async (movieId) => {
    try {
      await axios.post(`${API}/watchlist/${movieId}`, {}, { withCredentials: true });
      toast.success('Removed from Wish List');
      fetchAll();
    } catch (error) {
      toast.error('Failed to remove');
    }
  };

  const handleUnlike = async (movieId) => {
    try {
      await axios.post(`${API}/likes/${movieId}`, {}, { withCredentials: true });
      toast.success('Unliked');
      fetchAll();
    } catch (error) {
      toast.error('Failed');
    }
  };

  const handleReturnBorrow = async (borrowId) => {
    try {
      await axios.post(`${API}/borrows/${borrowId}/return`, {}, { withCredentials: true });
      toast.success('Movie returned');
      fetchAll();
    } catch (error) {
      toast.error('Failed to return');
    }
  };

  const handleCancelReservation = async (reservationId) => {
    if (!window.confirm('Cancel this pre-booking?')) return;
    try {
      await axios.delete(`${API}/reservations/${reservationId}`, { withCredentials: true });
      toast.success('Pre-booking cancelled');
      fetchAll();
    } catch (error) {
      toast.error('Failed to cancel');
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    if (pwNew !== pwConfirm) {
      toast.error('New passwords do not match');
      return;
    }
    if (pwNew.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    setChangingPw(true);
    try {
      await axios.post(
        `${API}/auth/change-password`,
        { current_password: pwCurrent, new_password: pwNew },
        { withCredentials: true }
      );
      toast.success('Password changed successfully');
      setPwCurrent(''); setPwNew(''); setPwConfirm('');
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Failed to change password');
    } finally {
      setChangingPw(false);
    }
  };

  const handleAvatarUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image must be under 5MB');
      return;
    }
    setUploadingAvatar(true);
    try {
      const form = new FormData();
      form.append('file', file);
      await axios.post(`${API}/users/me/avatar`, form, { withCredentials: true });
      toast.success('Profile photo updated');
      await checkAuth();
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Upload failed');
    } finally {
      setUploadingAvatar(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const activeBorrows = borrows.filter(b => b.status === 'active');

  const tabs = [
    { id: 'watchHistory', label: 'Watch History', icon: History, count: watchHistory.length },
    { id: 'liked', label: 'My Liked Movies', icon: Heart, count: liked.length },
    { id: 'watchlist', label: 'Wish List', icon: Bookmark, count: watchlist.length },
    { id: 'borrows', label: 'My Borrows', icon: BookMarked, count: activeBorrows.length },
    { id: 'reservations', label: 'My Pre-bookings', icon: CalendarClock, count: reservations.length },
    { id: 'searchHistory', label: 'Search History', icon: Search, count: searchHistory.length },
    { id: 'settings', label: 'User Settings', icon: Settings, count: null }
  ];

  const inputStyle = { backgroundColor: '#0F171E', border: '1px solid #2A3F52', color: '#FFFFFF' };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#0F171E' }}>
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2" style={{ borderColor: '#3B82F6' }}></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-24 px-4 sm:px-8 md:px-12 lg:px-16 pb-12" style={{ backgroundColor: '#0F171E' }} data-testid="user-profile-page">
      <div className="max-w-6xl mx-auto">
        <div className="flex items-center gap-5 mb-8 p-6 rounded-2xl" style={{ backgroundColor: '#1A2632', border: '1px solid #2A3F52' }}>
          <div className="relative">
            <div
              className="w-20 h-20 rounded-lg flex items-center justify-center text-3xl font-bold overflow-hidden"
              style={{ backgroundColor: '#1E3A8A', color: 'white' }}
              data-testid="profile-avatar"
            >
              {user?.avatar_path ? (
                <img src={mediaUrl(user.avatar_path, API)} alt={user?.name} className="w-full h-full object-cover" />
              ) : (
                user?.name?.[0]?.toUpperCase() || 'U'
              )}
            </div>
            <button
              onClick={() => fileInputRef.current?.click()}
              className="absolute -bottom-2 -right-2 w-8 h-8 rounded-full flex items-center justify-center transition-all hover:scale-110"
              style={{ backgroundColor: '#3B82F6', boxShadow: '0 2px 10px rgba(59, 130, 246, 0.5)' }}
              title="Change photo"
              disabled={uploadingAvatar}
              data-testid="profile-avatar-upload-btn"
            >
              <Camera size={14} color="white" />
            </button>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleAvatarUpload}
              className="hidden"
              data-testid="profile-avatar-file-input"
            />
          </div>
          <div>
            <h1 className="text-3xl font-bold" style={{ color: '#FFFFFF' }}>{user?.name}</h1>
            {user?.email && <p className="text-sm" style={{ color: '#8197A4' }}>{user?.email}</p>}
            <p className="text-xs mt-1 font-mono" style={{ color: '#3B82F6' }}>{user?.membership_id}</p>
          </div>
        </div>

        <div className="flex flex-wrap gap-2 mb-6">
          {tabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className="flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-all"
              style={{
                backgroundColor: activeTab === tab.id ? '#1E3A8A' : '#1A2632',
                color: activeTab === tab.id ? 'white' : '#8197A4',
                border: `1px solid ${activeTab === tab.id ? '#1E3A8A' : '#2A3F52'}`
              }}
              data-testid={`profile-tab-${tab.id}`}
            >
              <tab.icon size={16} />
              <span>{tab.label}</span>
              {tab.count !== null && (
                <span className="px-2 py-0.5 text-xs rounded-full" style={{
                  backgroundColor: activeTab === tab.id ? 'rgba(255,255,255,0.2)' : '#2A3F52',
                  color: activeTab === tab.id ? 'white' : '#8197A4'
                }}>
                  {tab.count}
                </span>
              )}
            </button>
          ))}
        </div>

        <div className="rounded-2xl p-6" style={{ backgroundColor: '#1A2632', border: '1px solid #2A3F52' }}>
          {activeTab === 'watchHistory' && (
            <div data-testid="watch-history-tab">
              <h2 className="text-2xl font-bold mb-4" style={{ color: '#FFFFFF' }}>Watch History</h2>
              {watchHistory.length === 0 ? (
                <p style={{ color: '#8197A4' }}>You haven&apos;t watched anything yet.</p>
              ) : (
                <div className="space-y-3">
                  {watchHistory.map((item) => (
                    <div key={item.movie_id}
                      className="flex items-center gap-4 p-3 rounded-lg cursor-pointer hover:bg-opacity-70"
                      style={{ backgroundColor: '#0F171E' }}
                      onClick={() => navigate(`/movie/${item.movie_id}`)}>
                      <img src={mediaUrl(item.movie?.poster_path, API) || `https://picsum.photos/seed/${item.movie_id}/60/90`}
                        alt={item.movie?.title} className="w-12 h-16 object-cover rounded" />
                      <div className="flex-1">
                        <h3 className="font-semibold" style={{ color: '#FFFFFF' }}>{item.movie?.title}</h3>
                        <p className="text-xs" style={{ color: '#8197A4' }}>Last watched {new Date(item.last_watched).toLocaleDateString()}</p>
                      </div>
                      <Play size={20} style={{ color: '#8197A4' }} />
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {activeTab === 'liked' && (
            <div data-testid="liked-tab">
              <h2 className="text-2xl font-bold mb-4" style={{ color: '#FFFFFF' }}>My Liked Movies</h2>
              {liked.length === 0 ? (
                <p style={{ color: '#8197A4' }}>You haven&apos;t liked any movies yet.</p>
              ) : (
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
                  {liked.map((item) => (
                    <div key={item.like_id || item.movie_id}
                      className="rounded-lg overflow-hidden relative movie-card-hover cursor-pointer"
                      style={{ backgroundColor: '#0F171E' }}>
                      <div className="relative" style={{ aspectRatio: '2/3' }} onClick={() => navigate(`/movie/${item.movie_id}`)}>
                        <img src={mediaUrl(item.movie?.poster_path, API) || `https://picsum.photos/seed/${item.movie_id}/400/600`}
                          alt={item.movie?.title} className="w-full h-full object-cover" />
                        <button onClick={(e) => { e.stopPropagation(); handleUnlike(item.movie_id); }}
                          className="absolute top-2 right-2 p-2 rounded-full hover:scale-110"
                          style={{ backgroundColor: 'rgba(239, 68, 68, 0.9)' }}
                          data-testid={`unlike-${item.movie_id}`}>
                          <Heart size={14} color="white" fill="white" />
                        </button>
                      </div>
                      <div className="p-3">
                        <h3 className="font-semibold text-sm truncate" style={{ color: '#FFFFFF' }}>{item.movie?.title}</h3>
                        <p className="text-xs" style={{ color: '#8197A4' }}>{item.movie?.release_year}</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {activeTab === 'watchlist' && (
            <div data-testid="watchlist-tab">
              <h2 className="text-2xl font-bold mb-4" style={{ color: '#FFFFFF' }}>Wish List</h2>
              {watchlist.length === 0 ? (
                <p style={{ color: '#8197A4' }}>Your wish list is empty.</p>
              ) : (
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
                  {watchlist.map((item) => (
                    <div key={item.watchlist_id}
                      className="rounded-lg overflow-hidden relative movie-card-hover cursor-pointer"
                      style={{ backgroundColor: '#0F171E' }}>
                      <div className="relative" style={{ aspectRatio: '2/3' }} onClick={() => navigate(`/movie/${item.movie_id}`)}>
                        <img src={mediaUrl(item.movie?.poster_path, API) || `https://picsum.photos/seed/${item.movie_id}/400/600`}
                          alt={item.movie?.title} className="w-full h-full object-cover" />
                        <button onClick={(e) => { e.stopPropagation(); handleRemoveFromWatchlist(item.movie_id); }}
                          className="absolute top-2 right-2 p-2 rounded-full hover:scale-110"
                          style={{ backgroundColor: 'rgba(220, 38, 38, 0.9)' }}>
                          <Trash2 size={14} color="white" />
                        </button>
                      </div>
                      <div className="p-3">
                        <h3 className="font-semibold text-sm truncate" style={{ color: '#FFFFFF' }}>{item.movie?.title}</h3>
                        <p className="text-xs" style={{ color: '#8197A4' }}>{item.movie?.release_year}</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {activeTab === 'borrows' && (
            <div data-testid="borrows-tab">
              <h2 className="text-2xl font-bold mb-4" style={{ color: '#FFFFFF' }}>
                My Borrows <span className="text-sm font-normal" style={{ color: '#8197A4' }}>({activeBorrows.length} active)</span>
              </h2>
              {borrows.length === 0 ? (
                <p style={{ color: '#8197A4' }}>You haven&apos;t borrowed any movies yet.</p>
              ) : (
                <div className="space-y-3">
                  {borrows.map((b) => {
                    const isActive = b.status === 'active';
                    const expiresAt = b.expires_at ? new Date(b.expires_at) : null;
                    return (
                      <div key={b.borrow_id} className="flex items-center gap-4 p-4 rounded-lg"
                        style={{ backgroundColor: '#0F171E', border: `1px solid ${isActive ? '#3B82F6' : '#2A3F52'}` }}
                        data-testid={`borrow-${b.borrow_id}`}>
                        <img src={mediaUrl(b.movie?.poster_path, API) || `https://picsum.photos/seed/${b.movie_id}/60/90`}
                          alt={b.movie?.title} className="w-12 h-16 object-cover rounded cursor-pointer"
                          onClick={() => navigate(`/movie/${b.movie_id}`)} />
                        <div className="flex-1 min-w-0">
                          <h3 className="font-semibold" style={{ color: '#FFFFFF' }}>{b.movie?.title || 'Unknown'}</h3>
                          <p className="text-xs" style={{ color: '#8197A4' }}>
                            Borrowed {new Date(b.borrowed_at).toLocaleDateString()}
                            {expiresAt && ` • Expires ${expiresAt.toLocaleDateString()}`}
                          </p>
                          <span className="inline-block mt-1 px-2 py-0.5 text-[10px] font-bold rounded-full uppercase"
                            style={{
                              backgroundColor: isActive ? 'rgba(59, 130, 246, 0.2)' : 'rgba(129, 151, 164, 0.2)',
                              color: isActive ? '#3B82F6' : '#8197A4'
                            }}>
                            {b.status}
                          </span>
                        </div>
                        {isActive && (
                          <button onClick={() => handleReturnBorrow(b.borrow_id)}
                            className="px-3 py-2 rounded-lg text-sm font-semibold hover:scale-105"
                            style={{ backgroundColor: 'rgba(220, 38, 38, 0.15)', color: '#EF4444', border: '1px solid #DC2626' }}
                            data-testid={`return-borrow-${b.borrow_id}`}>
                            Return
                          </button>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {activeTab === 'reservations' && (
            <div data-testid="reservations-tab">
              <h2 className="text-2xl font-bold mb-4" style={{ color: '#FFFFFF' }}>My Pre-bookings</h2>
              {reservations.length === 0 ? (
                <p style={{ color: '#8197A4' }}>You have no active pre-bookings.</p>
              ) : (
                <div className="space-y-3">
                  {reservations.map((r) => {
                    const scheduled = r.scheduled_date ? new Date(r.scheduled_date) : null;
                    const endDate = scheduled ? new Date(scheduled.getTime() + 5 * 24 * 60 * 60 * 1000) : null;
                    return (
                      <div key={r.reservation_id} className="flex items-center gap-4 p-4 rounded-lg"
                        style={{ backgroundColor: '#0F171E', border: '1px solid #10B981' }}
                        data-testid={`reservation-${r.reservation_id}`}>
                        <img src={mediaUrl(r.movie?.poster_path, API) || `https://picsum.photos/seed/${r.movie_id}/60/90`}
                          alt={r.movie?.title} className="w-14 h-20 object-cover rounded cursor-pointer"
                          onClick={() => navigate(`/movie/${r.movie_id}`)} />
                        <div className="flex-1 min-w-0">
                          <h3 className="font-semibold" style={{ color: '#FFFFFF' }}>{r.movie?.title || 'Unknown'}</h3>
                          <div className="flex flex-wrap gap-3 mt-1 text-xs" style={{ color: '#DDDDDD' }}>
                            <span><span style={{ color: '#8197A4' }}>Booked:</span> {new Date(r.created_at).toLocaleDateString()}</span>
                            {scheduled && (
                              <>
                                <span><span style={{ color: '#8197A4' }}>Start:</span> {scheduled.toLocaleDateString()}</span>
                                <span><span style={{ color: '#8197A4' }}>End:</span> {endDate.toLocaleDateString()}</span>
                              </>
                            )}
                            {!scheduled && <span style={{ color: '#10B981' }}>Notified when a slot opens</span>}
                          </div>
                        </div>
                        <button onClick={() => handleCancelReservation(r.reservation_id)}
                          className="flex items-center gap-1 px-3 py-2 rounded-lg text-sm font-semibold hover:scale-105"
                          style={{ backgroundColor: 'rgba(220, 38, 38, 0.15)', color: '#EF4444', border: '1px solid #DC2626' }}
                          data-testid={`cancel-reservation-${r.reservation_id}`}>
                          <X size={14} /> Cancel
                        </button>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {activeTab === 'searchHistory' && (
            <div data-testid="search-history-tab">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-2xl font-bold" style={{ color: '#FFFFFF' }}>Search History</h2>
                {searchHistory.length > 0 && (
                  <button onClick={handleClearSearchHistory}
                    className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold"
                    style={{ backgroundColor: 'rgba(220, 38, 38, 0.15)', color: '#EF4444', border: '1px solid #DC2626' }}
                    data-testid="clear-search-history">
                    <Trash2 size={16} /> Clear All
                  </button>
                )}
              </div>
              {searchHistory.length === 0 ? (
                <p style={{ color: '#8197A4' }}>No search history yet.</p>
              ) : (
                <div className="space-y-2">
                  {searchHistory.map((item) => (
                    <div key={item.search_id}
                      className="flex items-center justify-between p-3 rounded-lg cursor-pointer"
                      style={{ backgroundColor: '#0F171E' }}
                      onClick={() => navigate(`/search?q=${encodeURIComponent(item.query)}`)}>
                      <div className="flex items-center gap-3">
                        <Search size={16} style={{ color: '#8197A4' }} />
                        <span style={{ color: '#FFFFFF' }}>{item.query}</span>
                      </div>
                      <span className="text-xs" style={{ color: '#8197A4' }}>{new Date(item.created_at).toLocaleDateString()}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {activeTab === 'settings' && (
            <div data-testid="settings-tab" className="space-y-8">
              <div>
                <h2 className="text-2xl font-bold mb-4" style={{ color: '#FFFFFF' }}>Profile Photo</h2>
                <div className="flex items-center gap-6 flex-wrap">
                  <div className="w-24 h-24 rounded-lg flex items-center justify-center text-4xl font-bold overflow-hidden"
                    style={{ backgroundColor: '#1E3A8A', color: 'white' }}>
                    {user?.avatar_path ? (
                      <img src={mediaUrl(user.avatar_path, API)} alt={user?.name} className="w-full h-full object-cover" />
                    ) : (
                      user?.name?.[0]?.toUpperCase() || 'U'
                    )}
                  </div>
                  <div>
                    <button
                      onClick={() => fileInputRef.current?.click()}
                      disabled={uploadingAvatar}
                      className="flex items-center gap-2 px-4 py-3 rounded-lg font-semibold hover:scale-105"
                      style={{ backgroundColor: '#1E3A8A', color: 'white' }}
                      data-testid="settings-upload-photo-btn"
                    >
                      <Camera size={16} />
                      {uploadingAvatar ? 'Uploading...' : (user?.avatar_path ? 'Change Photo' : 'Upload Photo')}
                    </button>
                    <p className="text-xs mt-2" style={{ color: '#8197A4' }}>PNG, JPG or WEBP. Max 5MB.</p>
                  </div>
                </div>
              </div>

              <div className="pt-6" style={{ borderTop: '1px solid #2A3F52' }}>
                <h2 className="text-2xl font-bold mb-4 flex items-center gap-2" style={{ color: '#FFFFFF' }}>
                  <Lock size={20} /> Change Password
                </h2>
                <form onSubmit={handleChangePassword} className="max-w-md space-y-4">
                  <div>
                    <label className="block text-sm font-medium mb-2" style={{ color: '#DDDDDD' }}>Current Password</label>
                    <input type="password" value={pwCurrent} onChange={(e) => setPwCurrent(e.target.value)}
                      className="w-full px-4 py-3 rounded-lg outline-none" style={inputStyle} required
                      data-testid="current-password-input" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2" style={{ color: '#DDDDDD' }}>New Password</label>
                    <input type="password" value={pwNew} onChange={(e) => setPwNew(e.target.value)}
                      className="w-full px-4 py-3 rounded-lg outline-none" style={inputStyle} required minLength="6"
                      data-testid="new-password-input" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2" style={{ color: '#DDDDDD' }}>Confirm New Password</label>
                    <input type="password" value={pwConfirm} onChange={(e) => setPwConfirm(e.target.value)}
                      className="w-full px-4 py-3 rounded-lg outline-none" style={inputStyle} required minLength="6"
                      data-testid="confirm-password-input" />
                  </div>
                  <button type="submit" disabled={changingPw}
                    className="px-6 py-3 rounded-lg font-semibold hover:scale-105"
                    style={{ backgroundColor: '#1E3A8A', color: 'white' }}
                    data-testid="change-password-submit">
                    {changingPw ? 'Changing...' : 'Change Password'}
                  </button>
                </form>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
